import {
  Badge,
  CopyToClipboard,
  Dropdown,
  FlexBox,
  Table,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { unwrapResult } from '@reduxjs/toolkit';
import { Component } from 'react';
import OnClickOutside from 'react-onclickoutside';

import { AddUserResponse } from 'components/usersModal';

import styles from '../../../styles/recipient-pill.module.scss';

type EmailRecipient = {
  name: string | null;
  email: string;
};

type PropT = {
  id: string;
  from: EmailRecipient;
  toList: Array<EmailRecipient>;
  ccList: Array<EmailRecipient>;
  bccList: Array<EmailRecipient>;
  isAssignedToMe: boolean;
  csid: number;

  arrayPush: (csid: number, field: string, value: any) => void;
  composeEmailFindUser: (payload: any) => Promise<any>;
  userModalAddUser: (payload: any) => Promise<any>;
};

type StateT = {
  isOpen: boolean;
};

class RecipientPill extends Component<PropT, StateT> {
  constructor(props: PropT) {
    super(props);
    this.state = { isOpen: false };
  }

  handlePillClick = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  handleClickOutside = () => {
    this.setState({ isOpen: false });
  };

  handleDropClick = (payload: {
    from: string | null;
    to: string | null;
    cc: string | null;
    bcc: string | null;
  }) => {
    const { from, to, cc, bcc } = payload;
    const { csid } = this.props;
    let emailName: any | null = null;
    let type: string | null = null;

    if (from) {
      type = 'from';
      emailName = from;
    } else if (to) {
      type = 'to';
      emailName = to;
    } else if (cc) {
      type = 'cc';
      emailName = cc;
    } else if (bcc) {
      type = 'bcc';
      emailName = bcc;
    }
    if (emailName && emailName.email) {
      this.props
        .composeEmailFindUser(emailName.email)
        .then((response) => {
          if (
            response.data &&
            Array.isArray(response.data) === true &&
            response.data[0] !== undefined &&
            response.data[0].hits !== undefined
          ) {
            const hits = response.data[0].hits;
            // User found.
            if (hits.length === 1) {
              const userHit = hits[0];
              const user: any = { id: userHit.id, email: userHit.email };
              if (userHit.display_name) {
                user.name = userHit.display_name;
              }
              const payloadUser = {
                user: user,
              };
              this.props.arrayPush(csid, type as any, payloadUser);
            } else if (hits.length > 1) {
              for (const hit of hits) {
                if (hit.email === emailName.email) {
                  const user: any = { id: hit.id, email: hit.email };
                  if (hit.display_name) {
                    user.name = hit.display_name;
                  }
                  const payloadUser = {
                    user: user,
                  };
                  this.props.arrayPush(csid, type as any, payloadUser);
                  break;
                } else {
                  const user: any = { id: null, email: emailName.email };
                  if (emailName.name) {
                    user.name = emailName.name;
                  }
                  const payloadUser = {
                    user: user,
                  };
                  this.props.arrayPush(csid, type as any, payloadUser);
                }
              }
            } else {
              const payloadUser: any = {
                user: { id: null, email: emailName.email },
              };
              if (emailName.name) {
                payloadUser.user.name = emailName.name;
              }
              this.props.arrayPush(csid, type as any, payloadUser);
            }
          } else {
            const newEndUser: any = {
              email: emailName.email,
              user_type: 'contact',
            };
            if (emailName.name) {
              newEndUser.name = emailName.name;
            }
            // Creates a new user.
            this.props
              .userModalAddUser(newEndUser)
              .then(unwrapResult)
              .then(({ data: newUser }: AddUserResponse) => {
                const user: any = {
                  id: newUser.id,
                  email: newUser.email,
                };
                if (newUser.name) {
                  user.name = newUser.name;
                }
                const payloadUser = {
                  user: user,
                };
                this.props.arrayPush(csid, type as any, payloadUser);
              })
              .catch((exception) => {
                console.warn('userModalAddUser exception', exception);
              });
          }
        })
        .catch((e) => {
          console.warn('Exception', e);
        });
    }
  };

  render() {
    const titleParts: string[] = [];
    if (this.props.toList.length > 0) titleParts.push('To');
    if (this.props.ccList.length > 0) titleParts.push('Cc');
    if (this.props.bccList.length > 0) titleParts.push('Bcc');
    const title = titleParts.join('/');

    const emailRecipients = {
      from: {
        label: 'From',
        list: this.props.from && this.props.from.email ? [this.props.from] : [],
      },
      to: {
        label: 'To',
        list: this.props.toList,
      },
      cc: {
        label: 'Cc',
        list: this.props.ccList,
      },
      bcc: {
        label: 'Bcc',
        list: this.props.bccList,
      },
    };

    return (
      <div className={styles.recipientPill}>
        <Badge
          className={styles.badge}
          variant="primary"
          size="small"
          onClick={this.handlePillClick}
        >
          {title}
        </Badge>

        <Dropdown
          fixed
          isOpen={this.state.isOpen}
          style={{ maxWidth: 400, maxHeight: 300, overflow: 'auto' }}
        >
          <Table
            className={styles.table}
            wrapperProps={{
              className: styles.tableWrapper,
            }}
          >
            <Table.Tbody>
              {Object.values(emailRecipients).map((recipient, i) => {
                const { label, list } = recipient;

                return (
                  list.length > 0 && (
                    <Table.Tr key={`${label}-${i}`}>
                      <Table.Td>{label}</Table.Td>

                      <Table.Td>
                        <FlexBox gap="100" flexWrap="wrap">
                          {list.map((emailRecipient, i) => (
                            <Tooltip
                              key={`${emailRecipient.email}-${i}`}
                              content="Click to copy the email address"
                            >
                              <CopyToClipboard
                                tooltip
                                textToCopy={emailRecipient.email}
                              >
                                <Badge
                                  variant="secondary"
                                  size="medium"
                                  className={styles.badge}
                                >
                                  {emailRecipient.name
                                    ? `${emailRecipient.name} <${emailRecipient.email}>`
                                    : emailRecipient.email}
                                </Badge>
                              </CopyToClipboard>
                            </Tooltip>
                          ))}
                        </FlexBox>
                      </Table.Td>
                    </Table.Tr>
                  )
                );
              })}
            </Table.Tbody>
          </Table>
        </Dropdown>
      </div>
    );
  }
}

export default OnClickOutside(RecipientPill);
